/**
 * Что изменится у других целей, если тронуть одну.
 *
 * Цели делят одни и те же деньги. Поднять приоритет одной — значит отодвинуть
 * остальные, и человек имеет право увидеть это **до** согласия, а не через
 * месяц по факту. Раньше правка цели выглядела как безобидная настройка: срок
 * менялся, число в карточке росло, а то, что подушка от этого закрывается на
 * четыре месяца позже, нигде не произносилось.
 *
 * Здесь считаются два будущих — нынешнее и предложенное — по одним и тем же
 * правилам: сколько свободных денег есть в месяц, как они расходятся по
 * очереди целей, когда каждая закроется. Разница между ними и есть последствия.
 *
 * Важное ограничение честности: это **прогноз**, а не принятый план и не
 * факт. Он не переписывает прошлые планы, не двигает уже закреплённые деньги
 * и ничего не сохраняет. Сохранение — отдельное действие человека.
 */
import { ZERO, add, sub, type Money } from './money.js';
import { isAfter, type PlainDate } from './date.js';
import { allocateIncome } from './goals.js';
import { goalEta, monthlyNeedFor } from './goals.js';
import { computePlan, type PlanMode } from './plan.js';
import type { Account, CreditTerms, Goal, IncomeStream, Loan, Obligation } from './types.js';

/** Что человек хочет поменять в цели. */
export interface GoalChange {
  goalId: string;
  /** Новая цель целиком — либо `null`, если цель удаляется из планирования. */
  patch: Partial<Pick<Goal,
    'name' | 'targetAmount' | 'targetDate' | 'monthlyContribution' | 'priority' | 'status'
  >>;
}

/** Как цель выглядит в одном из двух будущих. */
export interface GoalOutlook {
  goalId: string;
  name: string;
  /** Сколько цель получает в месяц по очереди. */
  contribution: Money;
  /** Сколько нужно в месяц, чтобы успеть к сроку. */
  needed: Money;
  /** Когда закроется при этом взносе. `null` — не закроется вовсе. */
  eta: PlainDate | null;
  /** Успевает ли к названному сроку. `null` — срока нет. */
  onTime: boolean | null;
  status: Goal['status'];
}

/** Одна последствие для одной цели, словами, которые можно показать. */
export interface Consequence {
  goalId: string;
  name: string;
  text: string;
  /** Стало хуже для этой цели. Улучшения тоже называются, но иначе. */
  worse: boolean;
}

export interface ChangeEffect {
  before: GoalOutlook[];
  after: GoalOutlook[];
  consequences: Consequence[];
  /** Сколько свободных денег в месяц — одна и та же сумма в обоих будущих. */
  pot: Money;
  /** Сколько не хватает, чтобы все цели шли по своим срокам. */
  shortfall: Money;
  /** Чего расчёт не знает. Показывается рядом с числами. */
  limits: string[];
}

export interface ChangeInput {
  today: PlainDate;
  goals: readonly Goal[];
  change: GoalChange;
  incomeStreams: IncomeStream[];
  obligations: Obligation[];
  accounts: Account[];
  creditTerms: CreditTerms[];
  loans?: Loan[];
  needBaseline: Money;
  mode: PlanMode;
}

/** Цели после предложенного изменения. Исходный список не трогается. */
export const applyChange = (goals: readonly Goal[], change: GoalChange): Goal[] =>
  goals.map((g) => (g.id === change.goalId ? { ...g, ...change.patch } : g));

/**
 * Два будущих рядом: нынешнее и предложенное.
 *
 * Сумма, которую делят цели, в обоих будущих одна и та же — изменение цели
 * не создаёт денег. Меняется только то, кому они достаются и когда каждая
 * цель закроется.
 */
export function previewGoalChange(input: ChangeInput): ChangeEffect {
  const after = applyChange(input.goals, input.change);

  const plan = computePlan({
    incomeStreams: input.incomeStreams,
    obligations: input.obligations,
    needBaseline: input.needBaseline,
    goals: [...input.goals],
    accounts: input.accounts,
    creditTerms: input.creditTerms,
    mode: input.mode,
    today: input.today,
  });
  const pot = plan.amounts.save;

  const outlookBefore = outlooks(input, [...input.goals], pot);
  const outlookAfter = outlooks(input, after, pot);

  const limits = ['Прогноз при нынешнем доходе и обычных тратах'];
  if (pot <= ZERO) {
    limits.push('Свободных денег в месяц не остаётся — сроки целей посчитать не из чего');
  }

  return {
    before: outlookBefore,
    after: outlookAfter,
    consequences: compare(outlookBefore, outlookAfter, input.change.goalId),
    pot,
    shortfall: shortfallOf(outlookAfter, pot),
    limits,
  };
}

/** Как разойдутся деньги месяца и что из этого выйдет у каждой цели. */
function outlooks(input: ChangeInput, goals: Goal[], pot: Money): GoalOutlook[] {
  const { allocations } = allocateIncome({
    amount: pot,
    goals,
    accounts: input.accounts,
    creditTerms: input.creditTerms,
    monthlyNeed: input.needBaseline,
    today: input.today,
    ...(input.loans ? { loans: input.loans } : {}),
  });
  const got = new Map(allocations.map((a) => [a.goalId, a.amount]));

  return goals.map((g) => {
    const contribution = got.get(g.id) ?? ZERO;
    const eta = g.status === 'active' ? goalEta(g, contribution, input.today) : null;
    return {
      goalId: g.id,
      name: g.name,
      contribution,
      needed: monthlyNeedFor(g, input.today),
      eta,
      onTime: g.targetDate ? (eta !== null && !isAfter(eta, g.targetDate)) : null,
      status: g.status,
    };
  });
}

/** Сколько не хватает, чтобы каждая цель шла по своему сроку. */
const shortfallOf = (list: readonly GoalOutlook[], pot: Money): Money => {
  const needed = add(...list.filter((o) => o.status === 'active').map((o) => o.needed));
  return needed > pot ? sub(needed, pot) : ZERO;
};

/**
 * Разница между двумя будущими — словами.
 *
 * Названа только та цель, у которой что-то изменилось: перечислять всё
 * подряд значит утопить единственную важную строку среди пяти безразличных.
 */
function compare(
  before: readonly GoalOutlook[], after: readonly GoalOutlook[], changedId: string,
): Consequence[] {
  const out: Consequence[] = [];

  for (const a of after) {
    const b = before.find((x) => x.goalId === a.goalId);
    if (!b) continue;

    if (a.status !== b.status) {
      out.push({
        goalId: a.goalId, name: a.name, worse: a.status !== 'active',
        text: a.status === 'paused' ? 'Приостановлена: деньги пойдут другим целям'
          : a.status === 'achieved' ? 'Отмечена достигнутой'
          : a.status === 'cancelled' ? 'Убрана из планирования'
          : 'Снова активна и участвует в распределении',
      });
      continue;
    }
    if (a.status !== 'active') continue;

    const moneyChanged = a.contribution !== b.contribution;
    const dateChanged = a.eta !== b.eta;
    if (!moneyChanged && !dateChanged) continue;

    const later = a.eta !== null && b.eta !== null && isAfter(a.eta, b.eta);
    const stalled = a.eta === null && b.eta !== null;
    const parts: string[] = [];
    if (moneyChanged) {
      parts.push(a.contribution > b.contribution
        ? `получает на ${money(sub(a.contribution, b.contribution))} больше в месяц`
        : `получает на ${money(sub(b.contribution, a.contribution))} меньше в месяц`);
    }
    if (stalled) parts.push('перестаёт наполняться вовсе');
    else if (dateChanged && a.eta && b.eta) {
      parts.push(`срок сдвигается с ${human(b.eta)} на ${human(a.eta)}`);
    }
    if (a.onTime === false && b.onTime === true) parts.push('к названному сроку больше не успевает');

    out.push({
      goalId: a.goalId, name: a.name,
      worse: later || stalled || a.contribution < b.contribution,
      text: parts.join(', '),
    });
  }

  // Изменённая цель идёт первой: с неё человек и начал.
  return out.sort((x, y) => (x.goalId === changedId ? -1 : y.goalId === changedId ? 1 : 0));
}

const money = (m: Money): string => `${(m / 100).toLocaleString('ru-RU')} ₽`;

const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];
const human = (d: PlainDate): string => {
  const [y, m] = d.split('-');
  return `${MONTHS[Number(m) - 1]} ${y}`;
};
